<template>
  <div class="mb-10">
    <ActionableInsight
      :tabs="tabs"
      :documents="documents"
      :statements="statements"
      table-label="Unoptimized Live Jobs"
      :disable-share="isSharedPage"
      :is-shared-page="isSharedPage"
    >
      <template #noResultsFoundText>
        <h5>Nice Work!</h5>
        <p class="job-performance-no-result-content">
          It looks like your open job listings are ready to perform!
        </p>
      </template>
      <template #legend>
        <ActionableInsightLegend :description="legendDescription" :items="legendItems" />
      </template>
    </ActionableInsight>
  </div>
</template>

<script>
import { mapState, mapActions } from 'pinia';
import { useAppAuthData } from '@/stores/app/authData';
import { useInsights } from '@/stores/insights/insights';
import { useActionableInsights } from '@/stores/insights/actionableInsights';
import ActionableInsight from '@/components/insights/ActionableInsight/ActionableInsight.vue';
import ActionableInsightLegend from '@/components/insights/ActionableInsight/ActionableInsightLegend.vue';
import { useAppBase } from '@/stores/app/base';
import { useFilters } from '@/stores/filters/filters';
import { useSubMenu } from '@/stores/filters/subMenu';
import { useDatePicker } from '@/stores/filters/datePicker';
import { subscribeToFilterUpdates, unsubscribeToFilterUpdates } from '@/helpers/filterDispatcher';
import { useInsightsExport } from '@/stores/insights/export';
import { useInsightsShare } from '@/stores/insights/share';

export default {
  name: 'JobPerformanceReport',
  components: {
    ActionableInsight,
    ActionableInsightLegend,
  },
  emits: ['track-page-load'],
  data() {
    return {
      legendDescription: '',
      legendItems: [
        {
          mood: 'happy',
          description: `indicates that the job includes reporting structure (in an eligible job type), has an optimized job title, 
          includes years of experience (in an eligible job type), or does not include preferred requirements.`,
        },
        {
          mood: 'sad',
          description: `indicates that the job does not include reporting structure (in an eligible job type), has an unoptimized job title, 
          does not include years of experience (in an eligible job type), or includes preferred requirements.`,
        },
        {
          mood: 'neutral',
          description: `indicates that the job is not eligible for reporting structure or years of experience guidance 
          because it is a contractor/temporary position or is located in a geography where it is not common to include years of experience (often due to legal precedent for ageism).`,
        },
      ],
      tabLabels: [
        {
          label: 'Reporting Structure',
          key: 'percentageMissingReportingStructure',
          description: 'of your eligible and live jobs are missing reporting structure.',
          tooltipKey: 'confidence-gap-reporting-structure',
        },
        {
          label: 'Job Title Optimization',
          key: 'percentageUnoptimizedTitle',
          description: 'of your live jobs have unoptimized titles.',
          tooltipKey: 'job-performance-title-optimization',
        },
        {
          label: 'Years of Experience',
          key: 'percentageMissingExperience',
          description: 'of your live and eligible jobs are missing the years of experience desired.',
          tooltipKey: 'confidence-gap-years-of-experience',
        },
        {
          label: 'Preferred Requirements',
          key: 'percentageMissingPreferredRequirements',
          description: 'of your live jobs include unnecessary preferred requirements.',
          tooltipKey: 'job-perf-pref-requirements',
        },
      ],
      statements: [
        {
          text: 'Reporting',
          align: 'center',
          value: 'issues.reporting_structure',
          class: 'missing-statement-header',
        },
        {
          text: 'Title',
          align: 'center',
          value: 'issues.unoptimized_title',
          class: 'missing-statement-header',
        },
        {
          text: 'Experience',
          align: 'center',
          value: 'issues.experience',
          class: 'missing-statement-header',
        },
        {
          text: 'Preferred',
          align: 'center',
          value: 'issues.preferred_requirements',
          class: 'missing-statement-header',
        },
      ],
      isSharedPage: false,
    };
  },
  computed: {
    ...mapState(useActionableInsights, ['documents', 'totals']),
    ...mapState(useAppAuthData, ['authData']),
    ...mapState(useFilters, ['searchTerm', 'mergedTempFilters', 'myJobsEnabled']),
    ...mapState(useSubMenu, ['selectedSubMenuOption']),
    ...mapState(useDatePicker, ['dateRange']),
    tabs() {
      return this.tabLabels.map((tab) => {
        const value = this.totals[tab.key];

        if (value === undefined) {
          return {
            ...tab,
            description: 'No data available',
            value: 0,
            disabled: true,
          };
        }

        return {
          ...tab,
          description: tab.description,
          // Will show as <1% when percentage is less than 1% and greater than 0%
          value: value < 1 && value > 0 ? '<1' : Math.round(value),
        };
      });
    },
  },
  async mounted() {
    subscribeToFilterUpdates(this.loadPage);

    this.setFilterConfig({
      module: 'insights',
      datepicker: true,
      narrow: true,
      showMyJobs: !window?.sessionStorage?.reportingShareKey,
      subMenuType: 'chip',
    });

    this.$emit('track-page-load', { page: 'Job Performance Report' });

    const isSharedPage = window.sessionStorage.reportingShareKey && !this.authData.isLoggedIn;

    this.isSharedPage = Boolean(isSharedPage);

    if (isSharedPage) {
      this.showFilterBarSettings({ route: this.$route.name, options: ['hideDatePicker'] });
      this.resetExportConfig();
      this.resetShareConfig();
    } else {
      this.showFilterBarSettings({ route: this.$route.name, options: ['hideDatePicker'] });

      this.setExportConfig({
        csvExport: true,
        pngExport: false,
        pngWidgetOptions: {},
        csvOptions: [
          {
            label: 'Data Table (.csv)',
            csvDownloadFunction: () => {
              this.downloadJobPerformanceReport({
                filters: this.mergedTempFilters,
                dateRange: this.dateRange,
                myJobsEnabled: this.myJobsEnabled,
                document,
                sort: this.$route.query?.sort || this.selectedSubMenuOption,
              });
            },
            waitTime: 5000,
          },
        ],
      });

      this.setShareConfig({
        shareable: true,
      });
    }

    await this.loadPage({
      routeQuery: this.$route.query || '',
      page: this.$route.query?.page || 1,
      router: this.$router,
      sort: this.$route.query.sort,
      firstLoad: true,
      selectedMyJobs: this.$route.query?.owned === 'true',
      searchTerm: this.searchTerm,
      selectedSubMenuOption: this.selectedSubMenuOption,
    });
  },
  beforeUnmount() {
    unsubscribeToFilterUpdates();

    // Reset table reports data
    this.setActionableInsightsData();

    // Clean up filter config and selected sub menu option
    this.setFilterConfig({
      subMenuType: null,
    });

    this.resetExportConfig();
    this.resetShareConfig();

    this.setSelectedSubMenuOption(null);
  },
  methods: {
    ...mapActions(useAppBase, ['showFilterBarSettings']),
    ...mapActions(useInsights, ['loadPage']),
    ...mapActions(useFilters, ['setFilterConfig']),
    ...mapActions(useSubMenu, ['setSelectedSubMenuOption']),
    ...mapActions(useActionableInsights, ['setActionableInsightsData', 'downloadJobPerformanceReport']),
    ...mapActions(useInsightsExport, ['setExportConfig', 'resetExportConfig']),
    ...mapActions(useInsightsShare, ['setShareConfig', 'resetShareConfig']),
  },
};
</script>

<style lang="less" scoped>
.job-performance-no-result-content {
  max-width: 261px;
}
</style>
